import { API_URL } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Wallet, Users, Drop, Lightning, CreditCard, Storefront, GraduationCap,
  ChartPieSlice, ArrowsClockwise, Spinner, ArrowCircleUp,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { SkBar, SkeletonTable } from '../components/ui/page-skeleton';

/* ============================== UI atoms ============================== */

function KpiTile({ icon: Icon, label, accent, value, breakdown, testid, currency }) {
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden rounded-2xl bg-[#FFFFFF] border border-[#EDE6D8] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
    >
      <span className="absolute left-0 top-0 h-full w-[3px]" style={{ backgroundColor: accent }} />
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {label}
          </p>
          <p className="mt-2 text-2xl font-bold leading-none truncate" style={{ fontFamily: 'JetBrains Mono, monospace', color: accent }}>
            {value}
          </p>
          <p className="mt-1 text-[10px] uppercase tracking-wider text-[#A8A29E] min-h-[14px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {currency || '\u00A0'}
          </p>
        </div>
        <div className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
          <Icon size={18} style={{ color: accent }} weight="duotone" />
        </div>
      </div>
      {breakdown && breakdown.length > 0 && (
        <div
          className="mt-4 pt-4 border-t border-[#EDE6D8] grid gap-2"
          style={{ gridTemplateColumns: `repeat(${Math.min(breakdown.length, 3)}, minmax(0, 1fr))` }}
        >
          {breakdown.map((b) => (
            <div key={b.label}>
              <p className="text-[9px] uppercase tracking-wider text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{b.label}</p>
              <p className="text-sm font-bold text-[#0A2540] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{b.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, sub, height = 260, testid, children, className = '', action }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl bg-[#FFFFFF] border border-[#EDE6D8] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] ${className}`}
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</h3>
          {sub && <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{sub}</p>}
        </div>
        {action}
      </div>
      <div style={{ height }}>{children}</div>
    </div>
  );
}

function EmptyChart({ icon: Icon, label }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#EDE6D8] bg-gradient-to-br from-slate-50/60 to-white">
      {Icon && (
        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
          <Icon size={18} className="text-slate-400" weight="duotone" />
        </div>
      )}
      <p className="text-xs text-slate-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{label || 'No data yet'}</p>
    </div>
  );
}

/* ============================== utils ============================== */

const fmtCompact = (n) => {
  const x = Number(n || 0);
  if (Math.abs(x) >= 1_000_000_000) return `${(x / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(x) >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (Math.abs(x) >= 1_000) return `${(x / 1_000).toFixed(1)}K`;
  return x.toLocaleString(undefined, { maximumFractionDigits: 0 });
};
// Volumes (litres) are shown in full with thousands separators and exactly
// one decimal place — no K/M/B abbreviations. Per user request: "write it
// in full, also not approximate, include one decimal space".
const fmtLitres = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtInt = (n) => Number(n || 0).toLocaleString();

const PERF_COLORS = {
  excellent: '#10B981',
  good: '#0A2540',
  normal: '#B88E14',
  dormant: '#EF4444',
};

/* ============================== Page ============================== */

export default function DashboardPage() {
  const { user } = useAuth();
  const { selectedEntity } = useTenant();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  // Lightweight, independently-fetched donut so it paints in ~200ms without
  // waiting on the full dashboard payload (which fans out 5 upstream calls).
  const [rvrFast, setRvrFast] = useState(null);

  const entityIdParam = selectedEntity?.id || user?.entity_id || '';

  const fetchOverview = async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = entityIdParam ? { entity_id: entityIdParam } : {};
      const { data } = await axios.get(`${API_URL}/api/entity-dashboard/overview`, {
        withCredentials: true, headers, params,
      });
      setOverview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load dashboard');
      setOverview(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fire the donut request in parallel with the overview — it returns
  // first thanks to its lighter payload.
  const fetchRvrFast = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = entityIdParam ? { entity_id: entityIdParam } : {};
      const { data } = await axios.get(`${API_URL}/api/entity-dashboard/recharge-vs-refill`, {
        withCredentials: true, headers, params,
      });
      setRvrFast(data);
    } catch (e) {
      setRvrFast(null);
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchRvrFast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityIdParam]);

  const onRefresh = () => { setRefreshing(true); fetchOverview(true); fetchRvrFast(); };

  // Currency strictly from the dashboard payload (resolved server-side from
  // entity.currency_code → wallet trans hint → country_code ISO mapping).
  // Never fall back to a hardcoded "TZS" — surfacing the wrong sign is worse
  // than no sign at all and was misleading for non-TZ entities.
  const currency = overview?.entity?.currency || selectedEntity?.currency_code || '';
  const year = overview?.year || new Date().getFullYear();

  /* ---- derived chart series ---- */
  // Revenue per campus = recharges + subscription fees + card renewal fees.
  // The backend already sums these into `revenue` (with a back-compat fallback
  // to `recharge` for older payloads). Take the top 5 by revenue.
  // Skip any row that doesn't belong to a real campus — the backend buckets
  // orphaned/unmapped revenue under "Unknown" and we don't want that on the
  // chart as it's not actionable for admins.
  const campusBars = useMemo(() => {
    const excluded = new Set(['unknown', '—', '', 'unassigned', 'n/a']);
    return (overview?.campus_revenue || [])
      .filter((c) => {
        const nameRaw = (c.campus || '').trim();
        return nameRaw && !excluded.has(nameRaw.toLowerCase());
      })
      .map((c) => ({
        campus: c.campus,
        short: c.short_name || c.campus,
        Revenue: Number(
          c.revenue ??
          ((c.recharge || 0) + (c.subs_fee || 0) + (c.renewal_fee || 0))
        ),
      }))
      .sort((a, b) => b.Revenue - a.Revenue)
      .slice(0, 5);
  }, [overview]);

  // Muted, editorial palette — one color per bar (no shouting hues).
  const CAMPUS_BAR_COLORS = ['#0A2540', '#3D5A80', '#6B8B9E', '#A8B5BE', '#C9B79C'];

  // Prefer the fast standalone endpoint — falls back to the dashboard
  // payload (so the donut still renders correctly during initial load).
  const rvr = rvrFast || overview?.recharge_vs_refill || { recharge: 0, refill: 0 };
  const rvrTotal = Number(rvr.recharge || 0) + Number(rvr.refill || 0);
  const rvrSlices = rvrTotal > 0
    ? [
        { name: 'Recharge', value: Number(rvr.recharge || 0), color: '#0A2540' },
        { name: 'Refill', value: Number(rvr.refill || 0), color: '#10B981' },
      ]
    : [];

  const perfBars = useMemo(() => {
    const p = overview?.subscriber_performance || {};
    const rows = [
      { name: 'Excellent', Total: Number(p.excellent || 0), color: PERF_COLORS.excellent },
      { name: 'Good',      Total: Number(p.good || 0),      color: PERF_COLORS.good },
      { name: 'Normal',    Total: Number(p.normal || 0),    color: PERF_COLORS.normal },
      { name: 'Dormant',   Total: Number(p.dormant || 0),   color: PERF_COLORS.dormant },
    ];
    const sum = rows.reduce((s, r) => s + r.Total, 0) || 0;
    return rows.map((r) => ({ ...r, pct: sum > 0 ? (r.Total / sum) * 100 : 0, _sum: sum }));
  }, [overview]);

  /* ---- render ---- */
  if (loading) {
    return (
      <div className="space-y-4" data-testid="entity-dashboard-loading">
        <div className="flex items-end justify-between">
          <SkBar className="h-8 w-44" />
          <SkBar className="h-9 w-32 rounded-full" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[0,1,2,3].map(i => (
            <div key={i} className="rounded-2xl border border-[#EDE6D8] bg-white p-5">
              <SkBar className="h-3 w-1/2 mb-3" />
              <SkBar className="h-8 w-2/3" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0,1].map(i => (
            <div key={i} className="rounded-2xl border border-[#EDE6D8] bg-white p-5 h-60"><SkBar className="h-full w-full" /></div>
          ))}
        </div>
        <SkeletonTable rows={5} cols={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8" data-testid="entity-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="dashboard-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#EDE6D8] text-[#0A2540] text-[11px] font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            <ArrowsClockwise size={12} weight="bold" className={refreshing ? 'animate-spin' : ''} />
            REFRESH
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3" data-testid="dashboard-error">
          {error}
        </div>
      )}

      {/* Top KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiTile
          testid="kpi-recharges"
          icon={ArrowCircleUp}
          label={`Total Recharges ${year}`}
          accent="#0A2540"
          value={fmtMoney(overview?.recharges?.year)}
          currency={currency}
          breakdown={[
            { label: 'Today', value: fmtMoney(overview?.recharges?.today) },
            { label: 'This Month', value: fmtMoney(overview?.recharges?.month) },
          ]}
        />
        <KpiTile
          testid="kpi-subscribers"
          icon={Users}
          label="Customer Base"
          accent="#1E3A5F"
          value={fmtInt(overview?.subscribers?.total)}
          breakdown={[
            { label: 'Today', value: fmtInt(overview?.subscribers?.today_new) },
            { label: 'Month', value: fmtInt(overview?.subscribers?.month_new) },
            { label: `${year}`, value: fmtInt(overview?.subscribers?.year_new) },
          ]}
        />
        <KpiTile
          testid="kpi-volume"
          icon={Drop}
          label={`Total Volume ${year}`}
          accent="#0A2540"
          value={fmtLitres(overview?.volume_litres?.year)}
          currency="LITRES"
          breakdown={[
            { label: 'Today', value: fmtLitres(overview?.volume_litres?.today) },
            { label: 'This Month', value: fmtLitres(overview?.volume_litres?.month) },
          ]}
        />
        <KpiTile
          testid="kpi-balances"
          icon={Wallet}
          label="Account Balances"
          accent="#0A2540"
          value={fmtMoney(overview?.balances?.wallet_balance)}
          breakdown={[
            { label: 'Card Balance', value: fmtInt(overview?.balances?.card_balance) },
          ]}
        />
      </div>

      {/* Campus revenue + Recharge vs Refill */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard
          testid="campus-revenue"
          className="lg:col-span-2"
          title={`Campus Revenue · ${year}`}
          sub={`TOP 5 · RECHARGE + FEES · ${currency}`}
        >
          {campusBars.length === 0 ? <EmptyChart icon={GraduationCap} label="No campus revenue yet" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={campusBars} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                <XAxis
                  dataKey="short"
                  tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fill: '#78716C' }}
                  stroke="#78716C"
                  interval={0}
                  tickLine={false}
                  axisLine={{ stroke: '#EDE6D8' }}
                />
                <YAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', fill: '#A8A29E' }} stroke="#A8A29E" tickFormatter={fmtCompact} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ fontFamily: 'Plus Jakarta Sans, sans-serif', border: '1px solid #EDE6D8', borderRadius: '8px' }}
                  labelStyle={{ color: '#0A2540', fontWeight: 600, marginBottom: 4 }}
                  formatter={(v) => [`${fmtMoney(v)} ${currency}`, 'Revenue']}
                  labelFormatter={(_label, payload) => (payload && payload[0]?.payload?.campus) || _label}
                  cursor={{ fill: '#0A254010' }}
                />
                <Bar dataKey="Revenue" radius={[6, 6, 0, 0]}>
                  {campusBars.map((_, i) => (
                    <Cell key={i} fill={CAMPUS_BAR_COLORS[i % CAMPUS_BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard testid="recharge-vs-refill" title="Recharge vs Refill" sub="MONTH-TO-DATE">
          {rvrSlices.length === 0 ? <EmptyChart icon={ChartPieSlice} label="No transactions yet" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rvrSlices} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" label={false} labelLine={false}>
                  {rvrSlices.map((s) => (<Cell key={s.name} fill={s.color} />))}
                </Pie>
                <Tooltip
                  formatter={(v, name) => [fmtMoney(v), name]}
                  contentStyle={{ fontFamily: 'Plus Jakarta Sans, sans-serif', border: '1px solid #EDE6D8', borderRadius: '8px' }}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Performance + Stores Sales + Subscription Fee */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard testid="subs-performance" title="Subscribers Performance" sub="USAGE PAST 30 DAYS" height={210}>
          {perfBars.every((b) => !b.Total) ? <EmptyChart icon={Users} label="No subscribers yet" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perfBars} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" width={70} />
                <Tooltip
                  contentStyle={{ fontFamily: 'Plus Jakarta Sans, sans-serif', border: '1px solid #EDE6D8', borderRadius: '8px' }}
                  labelStyle={{ color: '#0A2540', fontWeight: 600, marginBottom: 4 }}
                  cursor={{ fill: '#0A254010' }}
                  formatter={(_val, _name, ctx) => {
                    const total = Number(ctx?.payload?.Total || 0);
                    const pct = Number(ctx?.payload?.pct || 0);
                    return [`${fmtInt(total)} · ${Math.round(pct)}%`, 'Total'];
                  }}
                />
                <Bar dataKey="Total" radius={[0, 6, 6, 0]}>
                  {perfBars.map((b) => (<Cell key={b.name} fill={b.color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <PeriodTile
          testid="stores-sales"
          title={`Stores Sales · ${year}`}
          sub="REFILL · VOLUME + AMOUNT"
          icon={Storefront}
          accent="#10B981"
          data={overview?.stores_sales}
          currency={currency}
          dual
        />

        <PeriodTile
          testid="subscription-fee"
          title={`Subscription Fee · ${year}`}
          sub="FEE-PLAN SIGN-UPS"
          icon={CreditCard}
          accent="#0A2540"
          data={overview?.subscription_fees}
          currency={currency}
        />
      </div>
    </div>
  );
}

/* ====== Period tile (today/month/year stacked rows) ======
   `dual` mode: each row shows litres + amount side-by-side — used for
   Stores Sales so volumes and revenue are both visible at a glance.
   Rows stack vertically so even long values render fully without truncation. */
function PeriodTile({ testid, title, sub, icon: Icon, accent, data, currency, dual }) {
  const d = data || {};
  const rows = [
    { label: 'Today',      v: d.today, l: d.today_litres },
    { label: 'This Month', v: d.month, l: d.month_litres },
    { label: 'This Year',  v: d.year,  l: d.year_litres },
  ];
  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-[#FFFFFF] border border-[#EDE6D8] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] flex flex-col"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</h3>
          {sub && <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{sub}</p>}
        </div>
        <div className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
          <Icon size={18} style={{ color: accent }} weight="duotone" />
        </div>
      </div>
      <div className="flex-1 flex flex-col gap-2 pt-1">
        {rows.map((row) => (
          <div
            key={row.label}
            className="rounded-xl bg-slate-50/60 border border-[#EDE6D8] px-3.5 py-2.5 flex items-center justify-between gap-3"
          >
            <p
              className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold shrink-0"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {row.label}
            </p>
            {dual ? (
              <div className="flex items-baseline gap-4 min-w-0">
                <span
                  className="text-sm font-bold whitespace-nowrap"
                  style={{ fontFamily: 'JetBrains Mono, monospace', color: accent }}
                >
                  {fmtLitres(row.l)}
                  <span className="text-[9px] text-[#A8A29E] ml-0.5 font-semibold">L</span>
                </span>
                <span
                  className="text-sm font-semibold text-[#1C1917] whitespace-nowrap"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {fmtMoney(row.v)}
                  <span className="text-[9px] text-[#A8A29E] ml-1 font-semibold">{currency}</span>
                </span>
              </div>
            ) : (
              <span
                className="text-sm font-bold whitespace-nowrap"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: accent }}
              >
                {fmtMoney(row.v)}
                <span className="text-[9px] text-[#A8A29E] ml-1 font-semibold">{currency}</span>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
